/**
 * DV-AUTH-005: Consolidated RequireAuth Route Guard
 *
 * Uses useServerAuth hook for session state - eliminates duplicate fetches
 * and React hooks invariant errors during navigation
 */

import { useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import LoadingScreen from '@/components/ui/LoadingScreen';
import { useServerAuth } from '@/hooks/useAuth-cookie';

export default function RequireAuth() {
  const location = useLocation();
  const { loading, isAuthenticated, user, error } = useServerAuth();
  const [hasRedirected, setHasRedirected] = useState(false);

  // Build redirect URL preserving current path
  const buildRedirectUrl = () => {
    const fullPath = location.pathname + location.search + location.hash;
    const redirect = encodeURIComponent(fullPath);
    return `/login?redirect_to=${redirect}`;
  };

  // Show loading during auth check
  if (loading) {
    return <LoadingScreen />;
  }

  // User is authenticated - render protected content
  if (isAuthenticated && user) {
    return <Outlet />;
  }

  // User is not authenticated - redirect to login
  if (!isAuthenticated) {
    // Prevent redirect loops - only redirect once per component instance
    if (!hasRedirected) {
      setHasRedirected(true);
      return <Navigate to={buildRedirectUrl()} replace />;
    }
    // If we've already redirected, show a message (shouldn't happen in normal flow)
    return <div data-testid="redirect-loop-protection">Redirecting to login...</div>;
  }

  // Error state - show loading (useServerAuth handles retry logic)
  if (error) {
    return <LoadingScreen />;
  }

  // Fallback
  return <LoadingScreen />;
}


